import { useMemo, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cancelBookingAsAdmin, getBookings, getCourses } from '@/lib/api'
import { DEFAULT_TABLE_PAGE_SIZE, TABLE_PAGE_SIZE_OPTIONS } from '@/lib/constants'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'

type SortKey = 'session' | 'name' | 'email'
type SortDirection = 'asc' | 'desc'

export function AdminBookingsPage() {
  const queryClient = useQueryClient()
  const [courseFilter, setCourseFilter] = useState<string | 'all'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('session')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE)

  const queryParams = useMemo(
    () => ({
      statusFilter: 'confirmed' as const,
      courseIdFilter: courseFilter,
      searchTerm: searchTerm.trim(),
      sortKey,
      sortDirection,
      page,
      pageSize,
    }),
    [courseFilter, page, pageSize, searchTerm, sortDirection, sortKey],
  )

  const coursesQuery = useQuery({
    queryKey: queryKeys.courses,
    queryFn: () => getCourses(true),
  })

  const sortedCourses = useMemo(() => {
    return [...(coursesQuery.data ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }),
    )
  }, [coursesQuery.data])

  const bookingsQuery = useQuery({
    queryKey: queryKeys.bookings(queryParams),
    queryFn: () => getBookings(queryParams),
    placeholderData: keepPreviousData,
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => cancelBookingAsAdmin(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.bookingsBase })
      toast.success('Buchung erfolgreich storniert.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const bookings = useMemo(() => bookingsQuery.data?.items ?? [], [bookingsQuery.data?.items])
  const totalBookings = bookingsQuery.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(totalBookings / pageSize))
  const currentPage = Math.min(page, totalPages)

  const handleSort = (nextKey: SortKey) => {
    if (sortKey === nextKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      setPage(1)
      return
    }

    setSortKey(nextKey)
    setSortDirection('asc')
    setPage(1)
  }

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) {
      return ''
    }
    return sortDirection === 'asc' ? ' ↑' : ' ↓'
  }

  return (
    <div className="space-y-4 xl:grid xl:h-full xl:min-h-0 xl:grid-rows-[auto_minmax(0,1fr)] xl:gap-4 xl:space-y-0 xl:overflow-hidden">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Buchungen</h1>
          <p className="text-sm text-muted-foreground">
            Bestätigte Buchungen prüfen und bei Bedarf stornieren.
          </p>
        </div>
      </div>

      {bookingsQuery.isError && (
        <p className="text-sm text-destructive">{getErrorMessage(bookingsQuery.error)}</p>
      )}

      <Card className="xl:flex xl:min-h-0 xl:max-h-full xl:w-full xl:self-start xl:flex-col xl:overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg">Aktuelle Buchungen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 xl:flex xl:min-h-0 xl:flex-col xl:overflow-hidden">
          <div className="grid gap-3 md:grid-cols-[1fr_200px_160px] xl:shrink-0">
            <Input
              placeholder="Nach Kurs, Name oder E-Mail filtern ..."
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value)
                setPage(1)
              }}
            />
            <Select
              value={courseFilter}
              onValueChange={(value) => {
                setCourseFilter(value)
                setPage(1)
              }}
            >
              <SelectTrigger className="bg-card">
                <SelectValue placeholder="Kurs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kurse</SelectItem>
                {sortedCourses.map((course) => (
                  <SelectItem key={course.id} value={course.id}>
                    {course.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => {
                setPageSize(Number(value))
                setPage(1)
              }}
            >
              <SelectTrigger className="bg-card">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TABLE_PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size} pro Seite
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Table containerClassName="max-h-[60dvh] xl:min-h-0 xl:flex-1 xl:max-h-none">
            <TableHeader>
              <TableRow>
                <TableHead>
                  <span className="font-medium">Kurs</span>
                </TableHead>
                <TableHead>
                  <button type="button" className="font-medium" onClick={() => handleSort('session')}>
                    Termin{sortIndicator('session')}
                  </button>
                </TableHead>
                <TableHead>
                  <button type="button" className="font-medium" onClick={() => handleSort('name')}>
                    Name{sortIndicator('name')}
                  </button>
                </TableHead>
                <TableHead>
                  <button type="button" className="font-medium" onClick={() => handleSort('email')}>
                    E-Mail{sortIndicator('email')}
                  </button>
                </TableHead>
                <TableHead className="text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell>{booking.course_sessions?.courses?.name ?? '-'}</TableCell>
                  <TableCell>
                    {booking.course_sessions?.starts_at
                      ? formatDateTime(booking.course_sessions.starts_at)
                      : '-'}
                  </TableCell>
                  <TableCell>{booking.full_name}</TableCell>
                  <TableCell>{booking.email}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => cancelMutation.mutate(booking.id)}
                      disabled={cancelMutation.isPending}
                    >
                      Stornieren
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {bookings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    Keine Buchungen gefunden.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between gap-3 text-sm text-muted-foreground xl:shrink-0">
            <p>
              {totalBookings === 0
                ? '0 Ergebnisse'
                : `${(currentPage - 1) * pageSize + 1} - ${Math.min(
                    (currentPage - 1) * pageSize + bookings.length,
                    totalBookings,
                  )} von ${totalBookings}`}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
              >
                Zurück
              </Button>
              <span>
                Seite {currentPage} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
              >
                Weiter
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
